import React, { Fragment, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import ErrorMessage from '../../components/errormessage';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { useStores } from '../../hooks/use-stores';
import { useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import SimpleTable from '../../components/super/SimpleTable';

function AssessmentTemplateQuestions({ templateId: propTemplateId }) {
    const { superStore } = useStores();
    let { templateId: paramTemplateId } = useParams();
    const templateId = propTemplateId || paramTemplateId;
    const isNew = templateId === 'create' || !templateId;
    const [publicFilter, setPublicFilter] = useState('all');
    const [loadingQuestionId, setLoadingQuestionId] = useState(null);
    const {
        assessmentTemplatesError,
        loadingQuestions,
        questions,
        loadingAssessmentTemplateQuestions,
        editingAssessmentTemplateQuestions,
        addAssessmentTemplateQuestion,
        deleteAssessmentTemplateQuestion,
    } = superStore;

    const isQuestionActive = (questionId) =>
        (editingAssessmentTemplateQuestions || []).some(
            (x) => String(x.question_id) === String(questionId)
        );

    const handleToggle = async (questionId, checked) => {
        setLoadingQuestionId(String(questionId));
        try {
            if (checked) {
                await addAssessmentTemplateQuestion(templateId, questionId);
                const question = questions.find(
                    (q) => String(q.id) === String(questionId)
                );
                if (
                    question &&
                    !editingAssessmentTemplateQuestions.find(
                        (q) => String(q.question_id) === String(questionId)
                    )
                ) {
                    editingAssessmentTemplateQuestions.push({
                        question_id: questionId,
                        name: question.name,
                        external_id: question.external_id,
                    });
                }
            } else {
                await deleteAssessmentTemplateQuestion(templateId, questionId);
                const index = editingAssessmentTemplateQuestions.findIndex(
                    (q) => String(q.question_id) === String(questionId)
                );
                if (index !== -1) {
                    editingAssessmentTemplateQuestions.splice(index, 1);
                }
            }
        } finally {
            setLoadingQuestionId(null);
        }
    };

    const filteredQuestions = useMemo(() => {
        let list = [...(questions || [])].sort((a, b) => b.id - a.id);
        if (publicFilter === 'public') {
            list = list.filter((q) => q.isPublic > 0);
        }
        return list;
    }, [questions, publicFilter]);

    const columns = [
        {
            Header: ' ',
            id: 'selected',
            disableSortBy: true,
            Cell: ({ row }) => {
                const questionId = row.original.id;
                if (loadingQuestionId === String(questionId)) {
                    return <CircularProgress size={24} />;
                }
                return (
                    <Checkbox
                        checked={isQuestionActive(questionId)}
                        onChange={(e) =>
                            handleToggle(questionId, e.target.checked)
                        }
                        disabled={loadingQuestionId != null}
                    />
                );
            },
        },
        {
            Header: 'Id',
            accessor: 'id',
        },
        {
            Header: 'Public',
            accessor: 'isPublic',
            Cell: (x) =>
                x.value > 0 ? (
                    <DoneIcon color="primary" />
                ) : (
                    <ClearIcon color="secondary" />
                ),
        },
        {
            Header: 'Name',
            accessor: 'name',
        },
    ];

    if (isNew || !templateId) {
        return (
            <Grid container direction="row" justify="center" align="center" spacing={2}>
                <Grid item xs={12}>
                    <Typography>Please save the assessment template first before adding questions.</Typography>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid
            container
            direction="row"
            justify="center"
            align="center"
            spacing={2}
        >
            {loadingQuestions || loadingAssessmentTemplateQuestions ? (
                <ThemedSpinner />
            ) : (
                <Fragment>
                    {assessmentTemplatesError && (
                        <Grid item>
                            <ErrorMessage error={assessmentTemplatesError} />
                        </Grid>
                    )}
                    {questions && (
                        <Fragment>
                            <Grid item xs={12}>
                                <Typography variant="h4" gutterBottom>
                                    Questions
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <SimpleTable
                                    columns={columns}
                                    data={filteredQuestions}
                                    needsFilter={true}
                                    extraFilter={
                                        <FormControl
                                            variant="outlined"
                                            style={{ minWidth: 140 }}
                                        >
                                            <InputLabel>Visibility</InputLabel>
                                            <Select
                                                value={publicFilter}
                                                onChange={(e) =>
                                                    setPublicFilter(
                                                        e.target.value
                                                    )
                                                }
                                                label="Visibility"
                                            >
                                                <MenuItem value="all">
                                                    All
                                                </MenuItem>
                                                <MenuItem value="public">
                                                    Public
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    }
                                />
                            </Grid>
                        </Fragment>
                    )}
                </Fragment>
            )}
        </Grid>
    );
}

export default observer(AssessmentTemplateQuestions);
